import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Clock, Users, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AfterActivityCard — Quiet acknowledgment of participation.
 * No friction. No pressure. No system feel.
 * "You showed up" → Keep this → SCUTA created invisibly.
 */
export default function AfterActivityCard() {
  const { user } = useContext(AuthContext);
  const [memories, setMemories] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/api/after/recent?limit=50`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMemories((r.data.memories || []).filter(m => !m.has_memory)))
      .catch(() => {});
  }, [user]);

  const handleKeep = async (activityId) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/after/keep`, { activity_id: activityId, note: noteText }, { headers: { Authorization: `Bearer ${token}` } });
      setMemories(prev => prev.filter(m => m.activity_id !== activityId));
      setActiveId(null);
      setNoteText('');
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  const handleDismiss = async (activityId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/after/dismiss`, { activity_id: activityId }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* silent */ }
    setMemories(prev => prev.filter(m => m.activity_id !== activityId));
  };

  if (!user || memories.length === 0) return null;

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  return (
    <>
      {memories.map(memory => {
        const locationStr = typeof memory.location === 'string' ? memory.location : '';
        const coText = memory.co_names?.length > 0 ? `You did this with ${memory.co_names.join(', ')}` : null;
        const isActive = activeId === memory.activity_id;
        return (
          <div
            key={memory.activity_id}
            className="mx-4 mt-3 px-4 py-3 rounded-xl"
            style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid={`after-activity-card-${memory.activity_id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>You showed up</p>
              <button
                onClick={() => handleDismiss(memory.activity_id)}
                className="p-0.5 rounded-full"
                style={{ color: 'var(--fy-text-disabled)' }}
                data-testid={`after-dismiss-${memory.activity_id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>{memory.title}</p>
            <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
              {locationStr && (
                <span className="flex items-center gap-0.5">
                  <MapPin className="w-3 h-3" />{locationStr.length > 25 ? locationStr.slice(0, 25) + '...' : locationStr}
                </span>
              )}
              {memory.time && (
                <span className="flex items-center gap-0.5">
                  <Clock className="w-3 h-3" />{formatTime(memory.time)}
                </span>
              )}
            </div>
            {coText && (
              <p className="text-xs mt-1.5" style={{ color: 'var(--fy-text-secondary)' }}>{coText}</p>
            )}
            {isActive && (
              <input
                type="text"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add a note..."
                maxLength={500}
                className="w-full mt-2 px-3 py-1.5 rounded-lg text-xs outline-none"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                data-testid={`after-note-input-${memory.activity_id}`}
                autoFocus
              />
            )}
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => handleKeep(memory.activity_id)}
                disabled={saving}
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: 'var(--lk-sage)', color: '#fff', opacity: saving ? 0.6 : 1 }}
                data-testid={`after-keep-${memory.activity_id}`}
              >
                {saving ? '...' : 'Keep this'}
              </button>
              {!isActive && (
                <button
                  onClick={() => setActiveId(memory.activity_id)}
                  className="text-xs"
                  style={{ color: 'var(--fy-text-disabled)' }}
                  data-testid={`after-add-note-${memory.activity_id}`}
                >
                  Add a note
                </button>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
